import * as React from 'react'
import { cn } from '../../lib/utils'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string
  helperText?: string
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
  /** When true, selects the entire field content on focus so typing overwrites the default value. */
  selectOnFocus?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, helperText, leftAddon, rightAddon, selectOnFocus, onFocus, onMouseDown, onBlur, ...props }, ref) => {
    // Tracks whether the upcoming focus comes from a mouse click. Only
    // mouse-initiated focus needs the mouseup guard below; keyboard focus
    // keeps its selection without interference.
    const mouseFocusArmed = React.useRef(false)
    return (
      <div className="w-full">
        <div className="relative flex items-center">
          {leftAddon && (
            <div className="pointer-events-none absolute left-3 flex items-center text-content-tertiary">
              {leftAddon}
            </div>
          )}
          <input
            ref={ref}
            className={cn(
              'flex h-10 w-full rounded-md border bg-surface-primary px-3 py-2 text-[16px] md:text-sm text-content-primary shadow-xs transition-colors duration-fast',
              'placeholder:text-content-tertiary',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error ? 'border-danger-500' : 'border-border-strong',
              leftAddon && 'pl-10',
              rightAddon && 'pr-10',
              className
            )}
            onMouseDown={(e) => {
              if (selectOnFocus) mouseFocusArmed.current = true
              onMouseDown?.(e)
            }}
            onBlur={(e) => {
              mouseFocusArmed.current = false
              onBlur?.(e)
            }}
            onFocus={(e) => {
              if (selectOnFocus) {
                e.target.select()
                if (mouseFocusArmed.current) {
                  // A mouse click places the caret on the subsequent mouseup,
                  // collapsing the selection just made. Swallow exactly that
                  // one mouseup; later clicks place the caret normally.
                  mouseFocusArmed.current = false
                  const el = e.target
                  const swallowMouseUp = (ev: MouseEvent) => {
                    ev.preventDefault()
                    el.removeEventListener('mouseup', swallowMouseUp)
                  }
                  el.addEventListener('mouseup', swallowMouseUp)
                }
              }
              onFocus?.(e)
            }}
            {...props}
          />
          {rightAddon && (
            <div className="absolute right-3 flex items-center text-content-tertiary">
              {rightAddon}
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-danger-500">{error}</p>}
        {helperText && !error && <p className="mt-1 text-xs text-content-tertiary">{helperText}</p>}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
